<template>
  <div 
    class="canvas-glass-card"
    :class="[
      `canvas-glass-card--${variant}`,
      `canvas-glass-card--${size}`,
      {
        'canvas-glass-card--hoverable': hoverable,
      }
    ]"
    :style="cardStyle"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Canvas 毛玻璃背景 -->
    <canvas
      ref="canvasRef"
      class="canvas-glass-background"
      :width="canvasWidth"
      :height="canvasHeight"
    ></canvas>
    
    <!-- 内容层 -->
    <div class="canvas-glass-content">
      <!-- 头部插槽 -->
      <div v-if="$slots.header || title" class="canvas-glass-card__header">
        <slot name="header">
          <h3 v-if="title" class="canvas-glass-card__title">{{ title }}</h3>
        </slot>
      </div>
      
      <!-- 主体内容 -->
      <div class="canvas-glass-card__body" :class="{ 
        'canvas-glass-card__body--no-padding': noPadding,
        'canvas-glass-card__body--no-scroll': disableBodyScroll
      }">
        <slot></slot>
      </div>
      
      <!-- 底部插槽 -->
      <div v-if="$slots.footer" class="canvas-glass-card__footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

// 定义 props
const props = defineProps({
  // 卡片标题
  title: {
    type: String,
    default: ''
  },
  // 卡片变体：primary, secondary, light, dark
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'light', 'dark'].includes(value)
  },
  // 卡片尺寸：small, medium, large
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  // 是否可悬停
  hoverable: {
    type: Boolean,
    default: false
  },
  // 是否显示边框
  bordered: {
    type: Boolean,
    default: true
  },
  // 是否显示阴影
  shadow: {
    type: Boolean,
    default: true
  },
  // 是否移除内边距
  noPadding: {
    type: Boolean,
    default: false
  },
  // 自定义宽度
  width: {
    type: [String, Number],
    default: ''
  },
  // 自定义高度
  height: {
    type: [String, Number],
    default: ''
  },
  // 自定义背景透明度 (0-1)
  opacity: {
    type: Number,
    default: 0.15,
    validator: (value) => value >= 0 && value <= 1
  },
  // 自定义模糊强度 (1-50)
  blur: {
    type: Number,
    default: 25,
    validator: (value) => value >= 1 && value <= 50
  },
  // 自定义饱和度 (50-300)
  saturate: {
    type: Number,
    default: 200,
    validator: (value) => value >= 50 && value <= 300
  },
  // 自定义圆角 (px)
  borderRadius: {
    type: Number,
    default: 20
  },
  // 是否启用 Canvas 毛玻璃效果
  useCanvasGlass: {
    type: Boolean,
    default: true
  },
  // 性能模式：轻量级渲染
  lightMode: {
    type: Boolean,
    default: false
  },
  // 禁用内部滚动（用于已有滚动区域的容器）
  disableBodyScroll: {
    type: Boolean,
    default: false
  }
})

// 响应式数据
const canvasRef = ref(null)
const canvasWidth = ref(0)
const canvasHeight = ref(0)
const isHovered = ref(false)
const animationId = ref(null)

// 变体配置 - 细边框优化版本
const variantConfigs = {
  primary: {
    backgroundColor: [255, 255, 255],
    backgroundOpacity: 0.12,
    borderColor: [74, 144, 226, 0.5], // 细边框适配透明度
    shadowColor: [0, 0, 0, 0.1]
  },
  secondary: {
    backgroundColor: [168, 200, 236],
    backgroundOpacity: 0.08,
    borderColor: [168, 200, 236, 0.6],
    shadowColor: [0, 0, 0, 0.08]
  },
  light: {
    backgroundColor: [255, 255, 255],
    backgroundOpacity: 0.2,
    borderColor: [52, 152, 219, 0.4], // 细边框适配
    shadowColor: [0, 0, 0, 0.06]
  },
  dark: {
    backgroundColor: [0, 0, 0],
    backgroundOpacity: 0.08,
    borderColor: [255, 255, 255, 0.5], // 细边框适配
    shadowColor: [0, 0, 0, 0.15]
  }
}

// 计算卡片样式
const cardStyle = computed(() => {
  const style = {}
  
  if (props.width) {
    style.width = typeof props.width === 'number' ? `${props.width}px` : props.width
  }
  
  if (props.height) {
    style.height = typeof props.height === 'number' ? `${props.height}px` : props.height
  }
  
  // 圆角
  style.borderRadius = `${props.borderRadius}px`
  style.overflow = 'hidden'
  
  // CSS边框样式 - 细边框设计
  if (props.bordered) {
    const config = variantConfigs[props.variant]
    const borderColor = config.borderColor
    style.border = `1px solid rgba(${borderColor[0]}, ${borderColor[1]}, ${borderColor[2]}, ${borderColor[3] * 0.6 || 0.4})`
    
    // 悬停时边框增强
    if (isHovered.value) {
      style.border = `1px solid rgba(${borderColor[0]}, ${borderColor[1]}, ${borderColor[2]}, ${(borderColor[3] * 0.8) || 0.6})`
    }
  }
  
  return style
})

// 创建毛玻璃效果 - 美观与性能平衡版本
const createGlassEffect = (ctx, config) => {
  const { backgroundColor, backgroundOpacity } = config
  
  // 创建半透明背景
  const bgColor = `rgba(${backgroundColor[0]}, ${backgroundColor[1]}, ${backgroundColor[2]}, ${backgroundOpacity * props.opacity})`
  
  // 绘制基础背景
  ctx.fillStyle = bgColor
  ctx.fillRect(0, 0, canvasWidth.value, canvasHeight.value)
  
  // 优化模糊层数：既保持效果又控制性能
  const blurLayers = props.lightMode ? 3 : 8 // 提升层数以改善效果
  
  // 模拟模糊效果 - 优化版本
  for (let i = 0; i < blurLayers; i++) {
    const alpha = props.lightMode 
      ? (0.008 * (props.saturate / 200)) / blurLayers
      : (0.015 * (props.saturate / 200)) / blurLayers
    
    ctx.fillStyle = `rgba(${backgroundColor[0]}, ${backgroundColor[1]}, ${backgroundColor[2]}, ${alpha})`
    
    const offset = props.lightMode ? i * 0.15 : i * 0.4
    ctx.fillRect(-offset, -offset, canvasWidth.value + offset * 2, canvasHeight.value + offset * 2)
  }
  
  // 添加轻微的光泽效果提升美观度
  if (!props.lightMode) {
    const gradient = ctx.createLinearGradient(0, 0, 0, canvasHeight.value)
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.2)')
    gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.05)')
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0.15)')
    
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, canvasWidth.value, canvasHeight.value)
  }
}

// 绘制边框
const drawBorder = (ctx, config) => {
  if (!props.bordered) return
  
  const { borderColor } = config
  
  // 设置边框样式
  ctx.strokeStyle = `rgba(${borderColor[0]}, ${borderColor[1]}, ${borderColor[2]}, ${borderColor[3] || 0.6})`
  ctx.lineWidth = 2 // 增加边框粗细
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  
  // 绘制圆角矩形边框
  const radius = props.borderRadius
  const x = 1
  const y = 1
  const width = canvasWidth.value - 2
  const height = canvasHeight.value - 2
  
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + width - radius, y)
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius)
  ctx.lineTo(x + width, y + height - radius)
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
  ctx.lineTo(x + radius, y + height)
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
  ctx.stroke()
  
  // 添加内发光效果增强边框可见性
  if (isHovered.value) {
    ctx.strokeStyle = `rgba(${borderColor[0]}, ${borderColor[1]}, ${borderColor[2]}, ${(borderColor[3] || 0.6) * 1.5})`
    ctx.lineWidth = 1
    ctx.stroke()
  }
}

// 绘制阴影效果 - 性能优化版本
const drawShadow = (ctx, config) => {
  if (!props.shadow) return
  
  // 简化阴影效果，移除复杂的径向渐变
  const { shadowColor } = config
  const shadowIntensity = isHovered.value ? 1.2 : 1
  
  // 使用简单的全局阴影效果
  ctx.fillStyle = `rgba(${shadowColor[0]}, ${shadowColor[1]}, ${shadowColor[2]}, ${shadowColor[3] * shadowIntensity * 0.3})`
  ctx.fillRect(0, 0, canvasWidth.value, canvasHeight.value)
}

// 渲染Canvas内容 - 添加节流机制
let renderTimer = null
const renderCanvas = () => {
  // 节流渲染，避免过于频繁的重绘
  if (renderTimer) {
    return
  }
  
  renderTimer = requestAnimationFrame(() => {
    const canvas = canvasRef.value
    if (!canvas) {
      renderTimer = null
      return
    }
    
    const ctx = canvas.getContext('2d')
    const config = variantConfigs[props.variant]
    
    // 清除画布
    ctx.clearRect(0, 0, canvasWidth.value, canvasHeight.value)
    
    if (props.useCanvasGlass) {
      // 使用Canvas实现毛玻璃效果
      createGlassEffect(ctx, config)
      drawShadow(ctx, config)
      // 移除Canvas边框绘制，改用CSS边框
    }
    
    renderTimer = null
  })
}

// 调整Canvas大小 - 添加防抖机制 + GPU优化
let resizeTimer = null
const resizeCanvas = () => {
  // 防抖处理，避免频繁重绘
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
  
  resizeTimer = setTimeout(() => {
    nextTick(() => {
      const canvas = canvasRef.value
      if (!canvas || !canvas.parentElement) return
      
      const rect = canvas.parentElement.getBoundingClientRect()
      // GPU优化：降低设备像素比，减少Canvas尺寸
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5) // 限制最大像素比
      
      canvasWidth.value = rect.width
      canvasHeight.value = rect.height
      
      // GPU优化：使用较低的分辨率
      canvas.width = rect.width * dpr * 0.75 // 降低75%分辨率
      canvas.height = rect.height * dpr * 0.75
      
      // 设置CSS显示大小
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
      
      // 缩放绘图上下文
      const ctx = canvas.getContext('2d')
      ctx.scale(dpr * 0.75, dpr * 0.75)
      
      // 重新渲染
      renderCanvas()
    })
  }, 32) // 增加防抖时间到32ms
}

// 鼠标事件处理 - 优化性能
const handleMouseEnter = () => {
  if (props.hoverable && !isHovered.value) {
    isHovered.value = true
    // 仅在有阴影时才重渲染
    if (props.shadow) {
      renderCanvas()
    }
  }
}

const handleMouseLeave = () => {
  if (props.hoverable && isHovered.value) {
    isHovered.value = false
    // 仅在有阴影时才重渲染
    if (props.shadow) {
      renderCanvas()
    }
  }
}

// 监听属性变化 - 优化性能
watch([
  () => props.variant,
  () => props.opacity,
  () => props.blur,
  () => props.saturate,
  () => props.shadow,
  () => props.useCanvasGlass
], () => {
  // 只在Canvas模式下才重渲染
  if (props.useCanvasGlass) {
    nextTick(renderCanvas)
  }
}, { deep: false }) // 禁用深度监听提升性能

// 组件挂载
onMounted(() => {
  window.addEventListener('resize', resizeCanvas)
  resizeCanvas()
})

// 组件卸载 - 清理所有定时器和动画帧
onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeCanvas)
  
  // 清理所有定时器
  if (resizeTimer) {
    clearTimeout(resizeTimer)
    resizeTimer = null
  }
  
  if (renderTimer) {
    cancelAnimationFrame(renderTimer)
    renderTimer = null
  }
  
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = null
  }
})
</script>

<style scoped>
.canvas-glass-card {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 允许 flex 子元素收缩 */
  /* 移除动画以提升性能 */
  /* Canvas 处理所有背景效果，边框使用 CSS 确保可见性 */
  box-sizing: border-box;
}

/* 边框增强样式 - 精致细边框 + 阴影效果 */
.canvas-glass-card--bordered {
  border: 1px solid rgba(74, 144, 226, 0.4) !important;
  box-shadow: 
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.3),
    0 0 0 0.5px rgba(74, 144, 226, 0.2),
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.04),
    0 4px 16px rgba(74, 144, 226, 0.08);
}

/* 悬停时边框增强 - 精致悬停效果 */
.canvas-glass-card--hoverable:hover {
  border-color: rgba(74, 144, 226, 0.6) !important;
  transform: scale(1.005) !important;
  transition: all 0.15s ease !important;
  box-shadow: 
    inset 0 0 0 0.5px rgba(255, 255, 255, 0.5),
    0 0 0 0.5px rgba(74, 144, 226, 0.4),
    0 4px 20px rgba(0, 0, 0, 0.12),
    0 2px 6px rgba(0, 0, 0, 0.08),
    0 8px 32px rgba(74, 144, 226, 0.15),
    0 0 20px rgba(74, 144, 226, 0.1);
}

.canvas-glass-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.canvas-glass-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; /* 允许 flex 子元素收缩 */
  width: 100%;
}

/* 尺寸变体 */
.canvas-glass-card--small .canvas-glass-card__body {
  padding: 16px;
}

.canvas-glass-card--small .canvas-glass-card__header {
  padding: 12px 16px 0;
}

.canvas-glass-card--small .canvas-glass-card__footer {
  padding: 0 16px 12px;
}

.canvas-glass-card--medium .canvas-glass-card__body {
  padding: 24px;
}

.canvas-glass-card--medium .canvas-glass-card__header {
  padding: 20px 24px 0;
}

.canvas-glass-card--medium .canvas-glass-card__footer {
  padding: 0 24px 20px;
}

.canvas-glass-card--large .canvas-glass-card__body {
  padding: 32px;
}

.canvas-glass-card--large .canvas-glass-card__header {
  padding: 28px 32px 0;
}

.canvas-glass-card--large .canvas-glass-card__footer {
  padding: 0 32px 28px;
}

/* 悬停效果 - 移除 transform 动画提升性能 */
.canvas-glass-card--hoverable:hover {
  /* 移除 transform 动画 */
}

/* 头部样式 */
.canvas-glass-card__header {
  flex-shrink: 0;
}

.canvas-glass-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

/* 主体样式 */
.canvas-glass-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 允许 flex 子元素收缩 */
}

/* 开启滚动的主体样式 */
.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll) {
  overflow-y: auto; /* 启用垂直滚动 */
  overflow-x: hidden; /* 防止水平滚动 */
}

.canvas-glass-card__body--no-padding {
  padding: 0 !important;
}

/* 滚动条美化 - 与项目风格保持一致 */
.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar {
  width: 6px;
}

.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  margin: 5px 0;
}

.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar-thumb {
  background: rgba(168, 200, 236, 0.4);
  border-radius: 3px;
  transition: background 0.3s ease;
}

.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 200, 236, 0.6);
}

/* 隐藏滚动条的上下箭头按钮 */
.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar-button {
  display: none;
}

.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll)::-webkit-scrollbar-corner {
  background: transparent;
}

/* Firefox 滚动条样式 */
.canvas-glass-card__body:not(.canvas-glass-card__body--no-scroll) {
  scrollbar-width: thin;
  scrollbar-color: rgba(168, 200, 236, 0.4) rgba(255, 255, 255, 0.1);
}

/* 底部样式 */
.canvas-glass-card__footer {
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  margin-top: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .canvas-glass-card--large .canvas-glass-card__body {
    padding: 20px;
  }
  
  .canvas-glass-card--large .canvas-glass-card__header {
    padding: 16px 20px 0;
  }
  
  .canvas-glass-card--large .canvas-glass-card__footer {
    padding: 0 20px 16px;
  }
  
  .canvas-glass-card--medium .canvas-glass-card__body {
    padding: 16px;
  }
  
  .canvas-glass-card--medium .canvas-glass-card__header {
    padding: 12px 16px 0;
  }
  
  .canvas-glass-card--medium .canvas-glass-card__footer {
    padding: 0 16px 12px;
  }
}
</style>